// Phantom Peek — captures the body/title of a (+) note that the
// spectator wrote and then swipe-deleted, and lets the magician
// trigger a brief, large-but-subtle reveal at the bottom of the
// Notes folder list during the natural beat of taking the phone
// back. Storage is sessionStorage so a tab close / page reload
// wipes the evidence; no localStorage footprint at all.
import { STORAGE } from "./storage";

const SESSION_KEY = "mr_peek_phrase";

/** Stash the captured phrase. Empty strings are silently ignored
 *  so an aborted (typed-nothing) compose+delete doesn't replace a
 *  previously-armed peek. */
export function armPeek(text) {
  const trimmed = (text || "").trim();
  if (!trimmed) return;
  try {
    sessionStorage.setItem(SESSION_KEY, trimmed);
  } catch { /* private mode / disabled storage — peek silently no-ops */ }
}

/** Read the armed peek without consuming. Returns "" when nothing
 *  is armed. */
export function readPeek() {
  try {
    return sessionStorage.getItem(SESSION_KEY) || "";
  } catch { return ""; }
}

/** Read + clear in a single call — used by the overlay after the
 *  reveal animation completes so a second tap on the trigger zone
 *  won't re-show the same word. */
export function consumePeek() {
  const v = readPeek();
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  return v;
}

/** Manually clear without consuming the return value — used by the
 *  Settings "test" / "clear" affordance. */
export function clearPeek() {
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
}

/** Magician-facing configuration. Defaults to enabled, 2s display. */
export function getPeekConfig() {
  const enabled = localStorage.getItem(STORAGE.PEEK_ENABLED);
  // Default ON when never configured — the feature is opt-in only via
  // the upstream (+ → delete) flow, so leaving the master switch off
  // surprises nobody.
  const isEnabled = enabled === null ? true : enabled === "1";
  const rawDur = parseInt(localStorage.getItem(STORAGE.PEEK_DURATION_MS) || "2000", 10);
  const duration = Number.isFinite(rawDur)
    ? Math.max(500, Math.min(5000, rawDur))
    : 2000;
  // Pass-motion is opt-in (off by default) because it needs iOS motion
  // permission and may misfire on hyper-active spectators.
  const passEnabled = localStorage.getItem(STORAGE.PEEK_PASS_MOTION) === "1";
  const rawThresh = parseInt(localStorage.getItem(STORAGE.PEEK_PASS_THRESHOLD) || "9", 10);
  const threshold = Number.isFinite(rawThresh)
    ? Math.max(6, Math.min(18, rawThresh))
    : 9;
  return {
    enabled: isEnabled,
    durationMs: duration,
    passMotionEnabled: passEnabled,
    passMotionThreshold: threshold,
  };
}
